/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import mapboxgl from 'mapbox-gl';

import { useBreakpoint } from 'sarsaparilla';

import { useMap, type RecenterOptions } from './MapContext';
import {
    CIRCLE_PIN_ZOOM_TRANSITION,
    defaultPadding,
    mobilePadding,
} from './map.constants';
import { isMapRemoved } from './mapGuards';

import type { MapMarkerItem } from './map.types';

const FEW_MARKERS_MAX_ZOOM = CIRCLE_PIN_ZOOM_TRANSITION + 1;
const MANY_MARKERS_MAX_ZOOM = 16;
const FEW_MARKERS_THRESHOLD = 3;

export interface UseFitBoundsToMarkersParams {
    data?: readonly MapMarkerItem[];
    /**
     * Called immediately before every programmatic fit the hook performs
     * (initial-data fit, resize fit, and provider-triggered `recenter`).
     * Consumers that run their own logic on `moveend` (e.g. search-on-map-move)
     * can use this to flag and ignore the `moveend` that a programmatic camera
     * move produces, so it isn't mistaken for a user gesture.
     */
    onBeforeFitBounds?: () => void;
    /**
     * Whether to fit with the taller mobile padding, which leaves room for an
     * overlay along the map's bottom (e.g. the search map's mobile card
     * carousel). Pass this when the consumer's mobile layout switches at a
     * different width than Sarsa's `isMdAndDown` (<992px). Defaults to
     * `isMdAndDown`.
     */
    isMobile?: boolean;
}

export interface UseFitBoundsToMarkersResult {
    fitBoundsToMarkers: (options?: RecenterOptions) => void;
}

/**
 * Frames the map around its markers: fits on the first data, refits whenever
 * the map resizes, and registers as the provider's `recenter` so siblings can
 * re-fit via `useMap()`. Must be used inside a `MapProvider`.
 */
export function useFitBoundsToMarkers({
    data,
    onBeforeFitBounds,
    isMobile,
}: UseFitBoundsToMarkersParams): UseFitBoundsToMarkersResult {
    const { mapInstance, registerRecenter } = useMap();
    const { isMdAndDown } = useBreakpoint();
    const shouldUseMobilePadding = isMobile ?? isMdAndDown;

    const fitBoundsToMarkers = React.useCallback(
        (options?: RecenterOptions) => {
            const { speed = 1.25, maxDuration = 4000 } = options ?? {};
            if (!mapInstance) return;
            if (!data?.length) return;
            if (isMapRemoved(mapInstance)) return;

            onBeforeFitBounds?.();

            try {
                const bounds = new mapboxgl.LngLatBounds();
                data.forEach((d) => {
                    const lng = parseFloat(d.longitude);
                    const lat = parseFloat(d.latitude);
                    if (Number.isNaN(lng) || Number.isNaN(lat)) return;
                    bounds.extend([lng, lat]);
                });

                const fitOptions: mapboxgl.FitBoundsOptions = {
                    padding: shouldUseMobilePadding ? mobilePadding : defaultPadding,
                    speed,
                    maxDuration,
                    offset: [0, 0],
                };

                // For a tiny set of markers we cap the zoom lower so the
                // camera keeps some surrounding context. If the user has
                // already zoomed in further than our cap, honor their zoom.
                const idealMaxZoom =
                    data.length < FEW_MARKERS_THRESHOLD
                        ? FEW_MARKERS_MAX_ZOOM
                        : MANY_MARKERS_MAX_ZOOM;
                if (mapInstance.getZoom() < idealMaxZoom) {
                    fitOptions.maxZoom = idealMaxZoom;
                }

                mapInstance.fitBounds(bounds, fitOptions);
            } catch {
                // Swallow: Mapbox can throw 'Invalid LngLat object' during
                // desktop→mobile resize races between DOM removal and the
                // resize listener. See the legacy hook for context.
            }
        },
        [data, mapInstance, shouldUseMobilePadding, onBeforeFitBounds]
    );

    // Publish our implementation to the provider so siblings can call it via
    // `useMap().recenter`.
    React.useEffect(() => {
        registerRecenter(fitBoundsToMarkers);
    }, [registerRecenter, fitBoundsToMarkers]);

    // Fit on initial data availability and whenever the map resizes.
    const didFitInitialRef = React.useRef(false);
    React.useEffect(() => {
        if (!mapInstance) return;
        if (!data?.length) return;
        if (isMapRemoved(mapInstance)) return;

        if (!didFitInitialRef.current) {
            fitBoundsToMarkers();
            didFitInitialRef.current = true;
        }

        const onResize = () => fitBoundsToMarkers();
        mapInstance.on('resize', onResize);
        return () => {
            mapInstance.off('resize', onResize);
        };
    }, [mapInstance, data, fitBoundsToMarkers]);

    return React.useMemo(() => ({ fitBoundsToMarkers }), [fitBoundsToMarkers]);
}
